import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { User } from '@app/core/models/input/user.model';
import { ChangePasswordOutputDTO } from '@app/core/models/output/change-password-output-DTO';
import { CCGuestChangePasswordModel } from '@app/core/models/output/guest-change-password-DTO';
import { EnvironmentService } from '@app/environment.service';
import { TranslateCustomService } from '@app/shared/services/translate/translate.service';
import { BehaviorSubject, Observable, catchError, finalize, map, tap, throwError } from 'rxjs';
import { CacheImplService } from '../../../shared/services/cache/cache-impl.service';
import { NotificationService } from '../notification/notification.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly userSubject: BehaviorSubject<User | null>;
  public user: Observable<User | null>;

  constructor(
    public userService: UserService,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly env: EnvironmentService,
    private readonly cacheImplService: CacheImplService,
    private readonly translateCustom: TranslateCustomService,
    private readonly notificationService: NotificationService
  ) {
    this.userSubject = new BehaviorSubject(JSON.parse((this.cacheImplService.getItem('user') as string) ?? false));
    this.user = this.userSubject.asObservable();
    if (this.userValue?.uuid) {
      this.notificationService.joinUserQueue(this.userValue.uuid);
    }
  }

  public get userValue() {
    return this.userSubject.value;
  }

  public setUserValue(user: User | null) {
    this.userSubject.next(user);
  }

  login(username: string, password: string, token: string, captchaToken: string | null) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const url = token !== '' ? `${this.env.apiUrl}/login-ssi` : `${this.env.apiUrl}/login`;

    return this.http
      .post<User>(
        url,
        { username, password, token, captchaToken },
        { headers: headers, observe: 'response', withCredentials: true }
      )
      .pipe(
        map(response => {
          const user = response.body;

          if (!user) {
            throw new Error('No user data returned from login response');
          }

          this.cacheImplService.setItem('user', JSON.stringify(user));

          const translatedToAngularLocale = user.language?.locale?.replace('_', '-') ?? this.env.defaultLanguage;

          this.translateCustom.changeLang(translatedToAngularLocale);
          this.userSubject.next(user);

          if (user?.uuid) {
            this.notificationService.joinUserQueue(user.uuid);
          }

          return response;
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 429) {
            const customError = {
              ...err,
              needsCaptcha: true,
            };
            return throwError(() => customError);
          }
          return throwError(() => err);
        })
      );
  }

  loginAdmin(username: string, password: string, captchaToken: string | null) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http
      .post<User>(
        `${this.env.apiUrl}/6ba7b812-9dad-11d1-80b4-00c04fd430c8`,
        { username, password, captchaToken },
        { headers: headers, observe: 'response' }
      )
      .pipe(
        map(response => {
          const user = response.body;
          if (!user) {
            throw new Error('No user data returned from login response');
          }
          this.cacheImplService.setItem('user', JSON.stringify(user));

          const translatedToAngularLocale = user.language?.locale?.replace('_', '-') ?? this.env.defaultLanguage;

          this.translateCustom.changeLang(translatedToAngularLocale);
          this.userSubject.next(user);

          return response;
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 429) {
            // (Too Many Requests)
            const customError = {
              ...err,
              needsCaptcha: true,
            };
            return throwError(() => customError);
          }
          return throwError(() => err);
        })
      );
  }

  resetPassword(guestChangePassword: CCGuestChangePasswordModel) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Password-Reset': 'true',
    });
    return this.http.post(`${this.env.apiUrl}/public/users/reset-password`, `${JSON.stringify(guestChangePassword)}`, {
      headers: headers,
      observe: 'response',
    });
  }

  public activateUser(): Observable<HttpResponse<object>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http.patch(this.env.apiUrl + '/users/activate', null, { headers: headers, observe: 'response' });
  }

  loginGuest(token: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http
      .post(`${this.env.apiUrl}/login-guest`, `{"token": ${JSON.stringify(token)}}`, {
        headers: headers,
        observe: 'response',
      })
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  public forceChangePassword(changePasswordOutputDTO: ChangePasswordOutputDTO): Observable<HttpResponse<object>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: this.cacheImplService.getItem('token') ?? '',
    });
    return this.http.put(this.env.apiUrl + '/users/reset-password', JSON.stringify(changePasswordOutputDTO), {
      headers: headers,
      observe: 'response',
    });
  }

  logout(redirectRoute: string | null = '/login') {
    const url = `${this.env.apiUrl}/logout`;

    this.http.post<void>(url, null, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        tap((res: HttpResponse<void>) => {
          if (res.status !== 204) {
            console.warn(`Logout API respondió ${res.status}`);
          }
        }),
        finalize(() => {
          this.cacheImplService.clear();
          this.userSubject.next(null);

          if (redirectRoute) {
            this.router.navigate([redirectRoute]);
          }
        })
      )
      .subscribe({
        error: err => {
          console.error('Fallo al contactar /logout; limpieza local hecha', err);
        }
      });
  }
}
